import { Alert, Box, Button, CircularProgress, Typography, useMediaQuery, useTheme } from '@mui/material';
import { styled } from '@mui/material/styles';
import { AxiosResponse } from 'axios';
import Dialog from 'components/dialog';
import List from 'components/list/list';
import ListCell from 'components/list/list-cell';
import ListRow from 'components/list/list-row';
import LoadingSpinner from 'components/loading-spinner';
import Paper from 'components/paper';

import RadioSelectItem from 'components/radio-select-item';
import Select from 'components/select';
import { useAccountState } from 'contexts/account/account-context';
import { SnackbarActionTypes, useSnackbarDispatch } from 'contexts/snackbar/SnackbarContext';
import dayjs from 'dayjs';
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import { useGet } from 'hooks/use-get';
import { usePost } from 'hooks/use-post';
import Mui from 'materials';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { FormattedDate, FormattedMessage, FormattedNumber, useIntl } from 'react-intl';
import { useQueryClient } from '@tanstack/react-query';
import { useRouteMatch } from 'react-router-dom';
import { OwnerType } from 'types/shared/owner-type.enum';
import _, { Dictionary } from 'underscore';
import getLineGraphOptions from 'utils/get-line-graph-options';
import titleCase from 'utils/title-case';
import { postCalculation } from 'views/planning-prognosis/api/create-calculation';
import { getPlanningPrognosisSite } from 'views/planning-prognosis/api/get-site';
import { GraphData } from 'views/planning-prognosis/features/view-fish-group/types/default-fish-group-graph.type';
import { FishGroupSnapshot } from 'views/planning-prognosis/features/view-fish-group/types/fish-group-snapshot.type';
import { getFishGroupSnapshot } from '../api/fish-group-snapshot/get-fish-group-snapshot';
import { getFishGroup, getInactiveFishGroup } from '../api/get-fish-group';
import { getFishGroupCalculationStatus } from '../api/get-fish-group-calculation-status';
import { getDefaultFishGroupGraph } from '../api/get-fish-group-graph';

const ActionGroup = styled(Box)(({ theme }) => ({
  display: 'flex',
}));

type ParamTypes = {
  localSiteId: string;
  fishGroupId: string;
  fishGroupTab: Tabs;
};

type TableData = {
  name: string;
  date: string;
  value: number;
};

enum Tabs {
  FISH_GROUPS = 'fishgroups',
  VIRTUAL = 'virtual',
  HARVESTED = 'harvested',
}

type Props = {
  fishGroupId: string;
  selectedSnapshot: FishGroupSnapshot | undefined;
};

const FishGroupGraph: React.FC<Props> = ({ fishGroupId, selectedSnapshot }) => {
  const intl = useIntl();
  const theme = useTheme();

  const PAGE_NAME_ID = 'planningPrognosis.siteDetails';

  // #region STATE

  const [periodisation, setPeriodisation] = useState(1);
  const [graphData, setGraphData] = useState<keyof GraphData>('feed');
  const { account } = useAccountState();
  const [refetchInterval, setRefetchInterval] = useState<number | false | undefined>(false);
  const previousRecalculating = useRef<boolean | undefined>();
  const queryClient = useQueryClient();
  const { params } = useRouteMatch<ParamTypes>();
  const snackbarDispatch = useSnackbarDispatch();
  // const [graphKeys] = useState(getGraphKeys());
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // #endregion STATE

  // #region FETCH DATA

  const { data: graph, isLoading: isLoadingGraph } = useGet(
    ['getDefaultFishGroupGraph', fishGroupId, periodisation],
    ({ queryKey }) => getDefaultFishGroupGraph(String(queryKey[1]), Number(queryKey[2])),
    {
      enabled: !!fishGroupId,
    }
  );

  const { data: snapshotData, isLoading: isLoadingSnapshotData } = useGet(
    ['getFishGroupSnapshot', fishGroupId, selectedSnapshot?.id, selectedSnapshot?.name],
    ({ queryKey }) => getFishGroupSnapshot(String(queryKey[1]), Number(queryKey[2]), String(queryKey[3])),
    {
      enabled: !!fishGroupId && !!selectedSnapshot,
    }
  );

  const { data: site } = useGet(
    ['getPlanningPrognosisSite', account.company?.companyId, params.localSiteId],
    (context) => getPlanningPrognosisSite(String(context.queryKey[1]), String(context.queryKey[2])),
    {
      enabled: !!account.company?.companyId && !!params.localSiteId,
    }
  );

  const { data: fishGroup } = useGet(
    ['getFishGroup', site?.globalSiteId, params.fishGroupId],
    (context) =>
      params.fishGroupTab === Tabs.HARVESTED
        ? getInactiveFishGroup(String(context.queryKey[1]), String(context.queryKey[2]))
        : getFishGroup(String(context.queryKey[1]), String(context.queryKey[2])),
    {
      enabled: !!site?.globalSiteId && !!params.fishGroupId,
    }
  );

  const { data: calculationStatus, refetch: refetchCalculationStatus } = useGet(
    ['getFishGroupCalculationStatus', fishGroup?.globalSiteId, fishGroup?.fishGroupId],
    (context) => getFishGroupCalculationStatus(String(context.queryKey[1]), String(context.queryKey[2])),
    {
      enabled: !!fishGroup?.globalSiteId && !!fishGroup?.fishGroupId,
      refetchInterval: refetchInterval,
    }
  );

  useEffect(() => {
    if (!calculationStatus) return;

    if (previousRecalculating.current === true && !calculationStatus.recalculating) {
      queryClient.invalidateQueries({
        queryKey: ['getFishGroup'],
      });
    }

    previousRecalculating.current = calculationStatus.recalculating;
    setRefetchInterval(calculationStatus.recalculating ? 10000 : false);
  }, [calculationStatus, queryClient]);

  const startCalculation = usePost<AxiosResponse, { owner: OwnerType; id: string }>(
    (request) => postCalculation(request.owner, request.id),
    () => {
      snackbarDispatch({
        type: SnackbarActionTypes.SUCCESS_SNACKBAR,
        payload: { msg: intl.formatMessage({ id: 'snackbar.success.default' }) },
      });
      refetchCalculationStatus();
    }
  );

  // #endregion FETCH DATA

  // #region RENDERS
  const getSnapshotData = (): Highcharts.SeriesOptionsType[] => {
    const arr: Highcharts.SeriesOptionsType[] = [];

    if (!!snapshotData) {
      arr.push(
        {
          type: 'line',
          name: selectedSnapshot?.name,
          dashStyle: 'ShortDash',
          color: '#017F62',
          data: snapshotData?.graph.actual.map((point) => ({
            x: dayjs(point.date).utc(true).valueOf(),
            y: Number(Number(point[graphData]).toFixed(0)),
          })),
          stack: 'Snapshot',
        },
        {
          type: 'line',
          name: 'Snapshot - Planned',
          dashStyle: 'ShortDash',
          color: theme.palette.bluViu.main,
          data: snapshotData?.graph.plan.map((point) => ({
            x: dayjs(point.date).utc(true).valueOf(),
            y: Number(Number(point[graphData]).toFixed(0)),
          })),
          stack: 'Snapshot',
          linkedTo: ':previous',
        },
        {
          type: 'line',
          name: 'Snapshot - Prognosis',
          dashStyle: 'ShortDash',
          color: '#A63C25',
          data: snapshotData?.graph.prognosis.map((point) => ({
            x: dayjs(point.date).utc(true).valueOf(),
            y: Number(Number(point[graphData]).toFixed(0)),
          })),
          stack: 'Snapshot',
          linkedTo: ':previous',
        }
      );
    }

    return arr;
  };

  // return only keys where prop type is number
  const keys = useMemo(() => {
    const gData = graph?.actual?.[0] || graph?.plan?.[0] || graph?.prognosis?.[0];
    let graphKeys: string[] = [];

    if (gData)
      Object.entries(gData).forEach(([k, v]) => {
        if (typeof v === 'number') graphKeys.push(k);
      });

    return graphKeys;
  }, [graph]);

  const getTableData = useCallback((): Dictionary<TableData[]> => {
    const arr: TableData[] = [];

    if (!!graph) {
      const actualArr: TableData[] = graph.actual.map((p) => ({
        date: p.date,
        value: p[graphData] as number,
        name: 'actual',
      }));
      arr.push(...actualArr);

      const plannedArr: TableData[] = graph.plan.map((p) => ({
        date: p.date,
        value: p[graphData] as number,
        name: 'plan',
      }));
      arr.push(...plannedArr);

      const prognosisArr: TableData[] = graph.prognosis.map((p) => ({
        date: p.date,
        value: p[graphData] as number,
        name: 'prognosis',
      }));
      arr.push(...prognosisArr);
    }

    return _.groupBy(arr, (d) => d.date);
  }, [graph, graphData]);

  // #endregion RENDERS

  // #region HELPERS

  const hasGraphData = !!graph && (!!graph?.actual.length || !!graph?.plan.length || !!graph?.prognosis.length);

  // #endregion HELPERS

  //TODO: Replace current Paper containing Graph with new FishGroupGraph component
  return (
    <>
      <Paper
        transparent
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: 4,
        }}>
        <Box display="flex" justifyContent="space-between" alignItems="flex-start">
          {/* TODO: add translations */}
          <Typography variant="h3" color={theme.palette.common.white}>
            Fish Group Data
          </Typography>
        </Box>

        {calculationStatus?.recalculating && (
          <Alert severity="success" icon={<CircularProgress size={18} />} style={{ marginBottom: 50 }}>
            <Mui.AlertTitle>
              <FormattedMessage id="planningPrognosis.alert.title" />
            </Mui.AlertTitle>
            <FormattedMessage id="planningPrognosis.alert.info" />
          </Alert>
        )}
        {hasGraphData && (
          <ActionGroup justifyContent={'flex-end'} gap={4} flexDirection={isMobile ? 'column' : 'row'}>
            <Select
              label={'Type'}
              value={graphData}
              style={{ minWidth: 150 }}
              onChange={(event) => setGraphData(event.target.value as keyof GraphData)}>
              {keys.map((key) => (
                <RadioSelectItem key={key} value={key} selected={key === graphData}>
                  {titleCase(key)}
                </RadioSelectItem>
              ))}
            </Select>
            <Select
              label="Periodisation"
              value={periodisation}
              style={{ minWidth: 150 }}
              onChange={(event) => setPeriodisation(Number(event.target.value))}>
              <Mui.MenuItem value={1}>Week</Mui.MenuItem>
              <Mui.MenuItem value={2}>Month</Mui.MenuItem>
            </Select>
            {!calculationStatus?.recalculating && account.company?.features.hasPlanningPrognosis && (
              <Button
                disabled={startCalculation.isPending}
                onClick={() => startCalculation.mutate({ owner: OwnerType.FISH_GROUP, id: fishGroupId })}>
                Recalculate
              </Button>
            )}
            <Dialog
              maxWidth="sm"
              title=""
              button={
                <Button>
                  <FormattedMessage id={`${PAGE_NAME_ID}.showTableBTN`} />
                </Button>
              }>
              <List
                dark
                options={[
                  { id: 'date', label: 'Date', align: 'left' },
                  { id: 'actual', label: 'Actual', align: 'right' },
                  { id: 'plan', label: 'Plan', align: 'right' },
                  { id: 'prognosis', label: 'Prognosis', align: 'right' },
                ]}>
                {Object.entries(getTableData()).map(([key, value]) => (
                  <ListRow key={key}>
                    <ListCell>
                      <FormattedDate value={key} day="2-digit" month="short" year="numeric" />
                    </ListCell>
                    <ListCell align="right">
                      <FormattedNumber
                        value={value.find((d) => d.name === 'actual')?.value ?? 0}
                        minimumFractionDigits={0}
                        maximumFractionDigits={0}
                      />
                    </ListCell>
                    <ListCell align="right">
                      <FormattedNumber
                        value={value.find((d) => d.name === 'plan')?.value ?? 0}
                        minimumFractionDigits={0}
                        maximumFractionDigits={0}
                      />
                    </ListCell>
                    <ListCell align="right">
                      <FormattedNumber
                        value={value.find((d) => d.name === 'prognosis')?.value ?? 0}
                        minimumFractionDigits={0}
                        maximumFractionDigits={0}
                      />
                    </ListCell>
                  </ListRow>
                ))}
              </List>
            </Dialog>
          </ActionGroup>
        )}
        {isLoadingGraph && <LoadingSpinner overlay />}
        <Paper>
          <HighchartsReact
            highcharts={Highcharts}
            options={
              {
                ...getLineGraphOptions({
                  xAxisTitle: 'DATE',
                  yAxisTitle: 'VALUE',
                  xAxisType: 'datetime',
                  formatNumber: intl.formatNumber,
                }),
                series:
                  isLoadingGraph || isLoadingSnapshotData
                    ? []
                    : ([
                        {
                          name: 'Actual',
                          dashStyle: 'Solid',
                          color: '#017F62',
                          data: graph?.actual.map((point) => ({
                            x: dayjs(point.date).utc(true).valueOf(),
                            y: Number(Number(point[graphData]).toFixed(0)),
                          })),
                        },
                        {
                          name: 'Planned',
                          dashStyle: 'ShortDot',
                          color: theme.palette.bluViu.main,
                          data: graph?.plan.map((point) => ({
                            x: dayjs(point.date).utc(true).valueOf(),
                            y: Number(Number(point[graphData]).toFixed(0)),
                          })),
                        },
                        {
                          name: 'Prognosis',
                          dashStyle: 'Solid',
                          color: '#A63C25',
                          data: graph?.prognosis.map((point) => ({
                            x: dayjs(point.date).utc(true).valueOf(),
                            y: Number(Number(point[graphData]).toFixed(0)),
                          })),
                        },
                        ...getSnapshotData(),
                      ] as Highcharts.SeriesOptionsType[]),
              } as Highcharts.Options
            }
          />
        </Paper>
      </Paper>
    </>
  );
};

export default FishGroupGraph;
